/**
 * CalGreen Compliance Utilities
 * Calculates waste diversion rates and compliance with CalGreen 5.408 requirements
 */

import type { WasteStream, ComplianceCalculation } from '../types';

/**
 * CalGreen 5.408 requires 65% diversion rate
 */
export const CALGREEN_DIVERSION_THRESHOLD = 65;

/**
 * Calculate compliance data from waste streams
 */
export function calculateCompliance(wasteStreams: WasteStream[]): ComplianceCalculation {
  const totalGenerated = wasteStreams.reduce(
    (sum, stream) => sum + stream.estimated_quantity,
    0
  );

  const totalDiverted = wasteStreams
    .filter(stream => stream.diversion_method !== 'landfill')
    .reduce((sum, stream) => sum + stream.estimated_quantity, 0);

  const diversionRate = totalGenerated > 0 ? (totalDiverted / totalGenerated) * 100 : 0;

  return {
    total_waste_generated: totalGenerated,
    total_waste_diverted: totalDiverted,
    diversion_rate: diversionRate,
    meets_calgreen_requirement: diversionRate >= CALGREEN_DIVERSION_THRESHOLD,
  };
}

/**
 * Check if waste streams meet CalGreen requirements
 */
export function meetsCalGreenRequirements(wasteStreams: WasteStream[]): boolean {
  const compliance = calculateCompliance(wasteStreams);
  return compliance.meets_calgreen_requirement;
}

/**
 * Get diversion rate color (green if compliant, red if not)
 */
export function getDiversionRateColor(diversionRate: number): string {
  return diversionRate >= CALGREEN_DIVERSION_THRESHOLD
    ? 'text-accent-600'
    : 'text-red-600';
}

/**
 * Get diversion rate background color
 */
export function getDiversionRateBgColor(diversionRate: number): string {
  return diversionRate >= CALGREEN_DIVERSION_THRESHOLD
    ? 'bg-accent-500'
    : 'bg-red-500';
}